import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { dashboardAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { isAdmin, canApprove, isAccountant } from "../lib/roles";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Receipt, CreditCard, CheckCircle, Clock, Plus, ArrowRight,
  User, Storefront, Bank, Wrench, LinkBreak, PaperPlaneTilt, ListChecks,
} from "@phosphor-icons/react";
import { formatDate } from "../lib/formatDate";
import { formatCurrency } from "../lib/formatCurrency";
import { toast } from "sonner";
import { SpendBarChart } from "../components/SpendBarChart";

const PERIODS = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "1y", label: "1 year" },
  { value: "all", label: "All time" },
];

function PeriodToggle({ value, onChange }) {
  return (
    <div className="inline-flex bg-zinc-100 rounded-sm p-0.5 border border-zinc-200" data-testid="period-toggle">
      {PERIODS.map((p) => (
        <button
          key={p.value}
          onClick={() => onChange(p.value)}
          data-testid={`period-${p.value}`}
          className={`px-2.5 sm:px-3 py-1 text-[11px] sm:text-xs font-semibold rounded-[3px] transition-all ${
            value === p.value
              ? "bg-white text-zinc-900 shadow-sm"
              : "text-zinc-500 hover:text-zinc-700"
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function KpiTile({ icon: Icon, label, count, amount, color = "text-zinc-600", onClick, testId, badgeText, badgeClass, highlight }) {
  const Wrap = onClick ? "button" : "div";
  return (
    <Wrap
      onClick={onClick}
      data-testid={testId}
      className={`p-4 sm:p-5 rounded-sm w-full text-left transition-all duration-200 ${
        highlight
          ? "bg-amber-50 border-2 border-amber-400 shadow-[0_0_0_4px_rgba(245,158,11,0.12)] animate-pulse-soft"
          : "bg-white border border-zinc-200"
      } ${onClick ? `${highlight ? "hover:border-amber-500" : "hover:border-[#002FA7]"} hover:shadow-md hover:-translate-y-[1px] cursor-pointer` : ""}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={16} weight={highlight ? "fill" : "duotone"} className={highlight ? "text-amber-600" : color} />
          <span className={`text-[10px] sm:text-[11px] uppercase tracking-[0.15em] font-bold ${highlight ? "text-amber-800" : "text-zinc-400"}`}>{label}</span>
        </div>
        {badgeText && (
          <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm ${badgeClass || "bg-zinc-100 text-zinc-500"}`}>
            {badgeText}
          </span>
        )}
      </div>
      <p className={`text-2xl sm:text-3xl font-bold tracking-tighter font-mono ${highlight ? "text-amber-900" : "text-zinc-950"}`} style={{ fontFamily: 'DM Sans, sans-serif' }}>
        {formatCurrency(amount || 0)}
      </p>
      <p className={`text-[11px] mt-0.5 ${highlight ? "text-amber-700" : "text-zinc-400"}`}>
        <span className={`font-semibold ${highlight ? "text-amber-800" : "text-zinc-600"}`}>{count || 0}</span> {count === 1 ? "item" : "items"}
      </p>
    </Wrap>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30d");
  const { user } = useAuth();
  const navigate = useNavigate();
  const admin = isAdmin(user);
  const approver = canApprove(user);
  const accountant = isAccountant(user);

  const fetchStats = async () => {
    try {
      const { data } = await dashboardAPI.stats(period);
      setStats(data);
    } catch (e) {
      console.error("Failed to fetch stats", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh: poll dashboard stats every 60s so the admin can leave the
  // dashboard up on a screen and watch numbers move in real time.
  useEffect(() => {
    const id = setInterval(() => { fetchStats(); }, 60000);
    return () => clearInterval(id);
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigation helpers — pass filter state via location.state so list pages can pre-apply quick filters
  const goExpenses = (extraState = {}) => navigate("/expenses", { state: extraState });
  const goApprovals = (extraState = {}) => navigate("/approvals", { state: extraState });
  const goTransactions = (extraState = {}) => navigate("/transactions", { state: extraState });

  if (loading) return (
    <div className="p-6 sm:p-8 animate-pulse space-y-6">
      <div className="h-8 bg-zinc-200 rounded w-48" />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {[1,2,3,4,5,6].map(i => <div key={i} className="h-28 bg-zinc-100 rounded-sm" />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[1,2,3,4].map(i => <div key={i} className="h-64 bg-zinc-100 rounded-sm" />)}
      </div>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {(admin || accountant) ? "Dashboard" : "My Dashboard"}
          </h1>
          <p className="text-sm text-zinc-500 mt-0.5">
            {accountant ? "Read-only view across the company" : admin ? "Real-time spend across the company" : "Your expenses at a glance"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <PeriodToggle value={period} onChange={setPeriod} />
          {!accountant && (
            <Button
              data-testid="new-expense-btn"
              onClick={() => navigate("/expenses/new")}
              className="bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm text-sm gap-1.5 shadow-sm hover:-translate-y-[1px] hover:shadow-md transition-all duration-200"
            >
              <Plus size={14} weight="bold" />
              New Expense
            </Button>
          )}
        </div>
      </div>

      {/* KPI Tiles — 6 cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <KpiTile
          icon={CreditCard}
          label="Card Swipes"
          count={stats?.card_swipes?.count}
          amount={stats?.card_swipes?.amount}
          color="text-blue-500"
          testId="kpi-card-swipes"
          onClick={() => goTransactions()}
        />
        <KpiTile
          icon={LinkBreak}
          label="Unlinked Swipes"
          count={stats?.unlinked_swipes?.count}
          amount={stats?.unlinked_swipes?.amount}
          color="text-amber-500"
          testId="kpi-unlinked-swipes"
          onClick={() => goTransactions({ linked: "false" })}
          badgeText={stats?.unlinked_swipes?.count > 0 ? "needs action" : null}
          badgeClass="bg-amber-100 text-amber-700"
        />
        <KpiTile
          icon={Receipt}
          label="Total Expenses"
          count={stats?.expenses_total?.count}
          amount={stats?.expenses_total?.amount}
          color="text-zinc-600"
          testId="kpi-expenses-total"
          onClick={() => goExpenses()}
        />
        <KpiTile
          icon={PaperPlaneTilt}
          label="Pending Submission"
          count={stats?.expenses_pending_submission?.count}
          amount={stats?.expenses_pending_submission?.amount}
          color="text-zinc-500"
          testId="kpi-pending-submission"
          onClick={() => goExpenses({ statusFilter: "draft,rejected" })}
          badgeText={stats?.expenses_pending_submission?.count > 0 ? "drafts + rejected" : null}
          badgeClass="bg-zinc-100 text-zinc-600"
        />
        <KpiTile
          icon={ListChecks}
          label="Awaiting Approval"
          count={stats?.expenses_awaiting_approval?.count}
          amount={stats?.expenses_awaiting_approval?.amount}
          color="text-amber-500"
          testId="kpi-awaiting-approval"
          onClick={() => approver ? goApprovals() : goExpenses({ statusFilter: "submitted" })}
          badgeText={approver && stats?.expenses_awaiting_approval?.count > 0 ? "review" : null}
          badgeClass="bg-amber-500 text-white"
          highlight={approver && stats?.expenses_awaiting_approval?.count > 0}
        />
        <KpiTile
          icon={CheckCircle}
          label="Approved"
          count={stats?.expenses_approved?.count}
          amount={stats?.expenses_approved?.amount}
          color="text-emerald-500"
          testId="kpi-approved"
          onClick={() => goExpenses({ statusFilter: "approved" })}
        />
      </div>

      {/* Spend breakdowns — bar charts */}
      <div className={`grid grid-cols-1 ${admin ? "lg:grid-cols-2 xl:grid-cols-3" : "lg:grid-cols-3"} gap-3 sm:gap-4 mb-6`}>
        {admin && (
          <SpendBarChart
            title="Unlinked Swipes by User"
            icon={LinkBreak}
            rows={(stats?.unlinked_swipes_by_user || []).map((r) => ({ ...r, key: r.user_id }))}
            onRowClick={(r) => goTransactions({ submitted_by: r.key, linked: "false" })}
            emptyText="No unlinked swipes — everything's allocated."
            testId="unlinked-swipes-by-user"
            amountClassName="text-amber-600"
          />
        )}
        {admin && (
          <SpendBarChart
            title="Spend by User"
            icon={User}
            rows={(stats?.spend_by_user || []).map((r) => ({ ...r, key: r.user_id }))}
            onRowClick={(r) => goTransactions({ submitted_by: r.key })}
            emptyText="No card spend in this period."
            testId="spend-by-user"
          />
        )}
        <SpendBarChart
          title="Spend by Vendor"
          icon={Storefront}
          rows={(stats?.spend_by_vendor || []).map((r) => ({ ...r, key: r.vendor }))}
          onRowClick={(r) => goExpenses({ vendor: r.key, statusFilter: "approved" })}
          emptyText="No approved spend in this period."
          testId="spend-by-vendor"
        />
        <SpendBarChart
          title="Spend by Overhead Account"
          icon={Bank}
          rows={(stats?.spend_by_gl || []).map((r) => ({ ...r, key: r.gl_account_id }))}
          onRowClick={(r) => goExpenses({ gl_account_id: r.key, statusFilter: "approved" })}
          emptyText="No GL-coded approved spend."
          testId="spend-by-gl"
        />
        <SpendBarChart
          title="Spend by Job"
          icon={Wrench}
          rows={(stats?.spend_by_job || []).map((r) => ({ ...r, key: r.job_id }))}
          onRowClick={(r) => goExpenses({ job_id: r.key, statusFilter: "approved" })}
          emptyText="No job-coded approved spend."
          testId="spend-by-job"
        />
        <SpendBarChart
          title="Spend By Job Budget Item"
          icon={ListChecks}
          rows={(stats?.spend_by_budget_item || []).map((r) => ({ ...r, key: r.budget_item_name }))}
          emptyText="No budget-item-coded spend yet."
          testId="spend-by-budget-item"
        />
      </div>

      {/* Recent Expenses */}
      <div className="bg-white border border-zinc-200 rounded-sm">
        <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
          <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-400">Recent Expenses</h3>
          <button onClick={() => navigate("/expenses")} className="text-xs text-[#002FA7] font-semibold hover:underline flex items-center gap-1" data-testid="view-all-expenses">
            View All <ArrowRight size={12} />
          </button>
        </div>
        {stats?.recent_expenses?.length > 0 ? (
          <div className="divide-y divide-zinc-100">
            {stats.recent_expenses.map((exp) => (
              <div key={exp.id} className="px-4 py-3 flex items-center justify-between hover:bg-zinc-50 transition-colors duration-200" data-testid={`recent-expense-${exp.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-800 truncate">{exp.vendor}</p>
                  <p className="text-xs text-zinc-400 mt-0.5">
                    {formatDate(exp.date)} {exp.expense_type === "job" ? `/ ${exp.job_name || "Job"}` : `/ ${exp.gl_account_name || "Overhead"}`}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold text-zinc-900 font-mono">{formatCurrency(exp.amount)}</span>
                  <Badge className={`text-[10px] uppercase tracking-wider rounded-sm ${
                    exp.status === "approved" ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
                    exp.status === "submitted" ? "bg-amber-100 text-amber-700 border-amber-200" :
                    exp.status === "rejected" ? "bg-red-100 text-red-700 border-red-200" :
                    "bg-zinc-100 text-zinc-500 border-zinc-200"
                  }`}>
                    {exp.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <Receipt size={32} weight="duotone" className="text-zinc-300 mx-auto mb-2" />
            <p className="text-sm text-zinc-400">No expenses in this period.</p>
          </div>
        )}
      </div>
    </div>
  );
}
